import { CheckCommons } from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ErrorSeverity, ErrorCode, FlexSdkErrorMetadata } from "~/modules/error";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";
import { ConversationImpl } from "~/modules/Conversation/ConversationImpl";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { Conversation } from "~/modules/Conversation";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

export async function handleConversationInitialization<K extends EVENTS>(
    conversations: ConversationsSdkImpl,
    checkCommons: CheckCommons<K>
): Promise<void> {
    try {
        await conversations.init();
    } catch (error) {
        const errorMsg = `${checkCommons.actionName}: Failed to initialize Conversations SDK`;
        checkCommons.logger.error(errorMsg, error);

        if (checkCommons.event && checkCommons.analytics) {
            sendWorkerTrackingEvent(checkCommons.event, checkCommons.analytics, checkCommons.worker, {
                ...checkCommons.extra,
                error: `${errorMsg} (${toCauseMessage(error)})`
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkCommons.moduleName,
            source: checkCommons.actionName
        };
        throwFlexSdkError(checkCommons.context)(
            ErrorCode.FailedToInitializeConversationsSDK,
            metadata,
            errorMsg,
            error
        );
    }
}

export async function handleGetConversationBySid<K extends EVENTS>(
    conversations: ConversationsSdkImpl,
    conversationSid: string,
    context: ContextManager,
    checkCommons: CheckCommons<K>
): Promise<Conversation> {
    try {
        const conversation = await conversations.getConversationBySid(conversationSid);
        if (checkCommons.event && checkCommons.analytics) {
            sendWorkerTrackingEvent(checkCommons.event, checkCommons.analytics, checkCommons.worker, {
                ...checkCommons.extra
            });
        }
        return new ConversationImpl(context, conversation);
    } catch (error) {
        const errorMsg = `${checkCommons.actionName}: Failed to retrieve conversation ${conversationSid}`;
        checkCommons.logger.error(errorMsg, error);
        if (checkCommons.event && checkCommons.analytics) {
            sendWorkerTrackingEvent(checkCommons.event, checkCommons.analytics, checkCommons.worker, {
                ...checkCommons.extra,
                error: `${errorMsg} (${toCauseMessage(error)})`
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkCommons.moduleName,
            source: checkCommons.actionName
        };
        return throwFlexSdkError(context)(ErrorCode.FailedToRetrieveConversation, metadata, errorMsg, error);
    }
}
